import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Briefcase, Clock, IndianRupee, GraduationCap, Users, Building2,
  CalendarClock, CheckCircle2, ArrowLeft, MessageCircle, Share2, ChevronRight, Star, Flame, UserCheck, Handshake,
} from "lucide-react";
import Breadcrumb from "@/components/Breadcrumb";
import ApplyForm from "@/components/jobs/ApplyForm";
import { COMPANY } from "@/data/content";
import { relTime } from "@/components/jobs/JobCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAT_BADGE = {
  hot: { label: "Hot Job", Icon: Flame, cls: "bg-red-50 text-red-600 border-red-100" },
  walkin: { label: "Walk-in", Icon: CalendarClock, cls: "bg-amber-50 text-amber-700 border-amber-100" },
  fresher: { label: "Freshers", Icon: UserCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
};

export default function JobDetailPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/jobs/${id}`)
      .then((r) => mounted && setJob(r.data))
      .catch((e) => mounted && setError(e?.response?.status === 404 ? "notfound" : "error"));
    return () => { mounted = false; };
  }, [id]);

  if (error === "notfound") {
    return (
      <section className="min-h-[60vh] pt-32 px-6 text-center">
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">Job not found</h1>
        <p className="mt-3 text-slate-600">This opening may have been closed or filled.</p>
        <Link to="/careers" className="mt-6 inline-flex px-5 py-2.5 rounded-lg bg-primary text-white font-semibold">Browse all jobs</Link>
      </section>
    );
  }
  if (!job) return <section className="min-h-[60vh] pt-32 px-6 text-center text-slate-500">Loading job…</section>;

  const tag = CAT_BADGE[job.category];
  const waMsg = encodeURIComponent(
    `Hi Advance HR, I'd like to apply for "${job.title}" (${job.location}). Please share next steps.`
  );

  return (
    <>
      <section className="pt-28 lg:pt-36 pb-10 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/careers" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-primary">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to all openings
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {job.is_featured && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-gradient-to-r from-primary to-blue-500 text-white">
                <Star className="w-3 h-3 fill-current" /> Featured
              </span>
            )}
            {tag && (
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border ${tag.cls}`}>
                <tag.Icon className="w-3 h-3" /> {tag.label}
              </span>
            )}
            <span className="text-[11px] uppercase tracking-wider font-semibold text-primary">{job.department}</span>
            <span className="text-[11px] text-slate-400">·</span>
            <span className="text-[11px] text-slate-500">Posted {relTime(job.posted_at)}</span>
          </div>

          <h1 className="mt-3 font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight tracking-tight">
            {job.title}
          </h1>
          {job.company_name && (
            <p className="mt-2 inline-flex items-center gap-2 text-sm text-slate-600">
              <Building2 className="w-4 h-4" /> {job.company_name}
            </p>
          )}

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <Meta icon={MapPin} label="Location" value={job.location} />
            <Meta icon={Clock} label="Experience" value={job.experience} />
            <Meta icon={IndianRupee} label="Salary" value={job.salary_range || "Negotiable"} />
            <Meta icon={Briefcase} label="Type" value={job.employment_type} />
            <Meta icon={GraduationCap} label="Qualification" value={job.qualification || "—"} />
            <Meta icon={Users} label="Openings" value={String(job.openings)} />
          </div>
        </div>
      </section>

      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Careers", to: "/careers" }, { label: job.title }]} />

      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left: details */}
          <div className="lg:col-span-2 space-y-10">
            <Block title="About the role">
              <p className="text-slate-600 leading-relaxed">{job.description}</p>
            </Block>

            {job.responsibilities?.length > 0 && (
              <Block title="Key Responsibilities">
                <Bullets items={job.responsibilities} />
              </Block>
            )}

            {job.requirements?.length > 0 && (
              <Block title="Requirements">
                <Bullets items={job.requirements} />
              </Block>
            )}

            {job.skills?.length > 0 && (
              <Block title="Skills Required">
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((s) => (
                    <span key={s} className="text-xs font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary border border-primary/15">{s}</span>
                  ))}
                </div>
              </Block>
            )}

            {job.benefits?.length > 0 && (
              <Block title="What you'll get">
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {job.benefits.map((b) => (
                    <div key={b} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={1.75} /> {b}
                    </div>
                  ))}
                </div>
              </Block>
            )}

            <Block title="Shift & Work Schedule">
              <p className="text-slate-600">{job.shift || "General Shift"}</p>
            </Block>
          </div>

          {/* Right: apply card */}
          <aside className="lg:col-span-1">
            <div id="apply" className="lg:sticky lg:top-28 space-y-4">
              <div className="bg-gradient-to-br from-primary to-blue-600 text-white rounded-3xl p-6 shadow-xl shadow-primary/20">
                <h3 className="font-heading font-bold text-xl leading-tight">Interested in this role?</h3>
                <p className="mt-2 text-sm text-white/85 leading-relaxed">
                  Apply directly with your resume. Our recruiters will reach out within 2 business days.
                </p>
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}?text=${waMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="job-whatsapp-apply"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] hover:bg-emerald-500 text-white font-semibold text-sm transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Apply via WhatsApp
                </a>
                <a
                  href="#apply-form"
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-sm border border-white/25 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" /> Apply with Form
                </a>
              </div>

              {/* Recruitment Partner sidebar card */}
              <div
                data-testid="job-partner-card"
                className="bg-white border border-primary/20 rounded-3xl p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <Handshake className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <h4 className="font-heading font-bold text-slate-900 text-base leading-tight">Recruitment Partner?</h4>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  Login to submit candidates for this position and earn the applicable recruitment commission.
                </p>
                <Link
                  to="/login"
                  data-testid="job-partner-login-cta"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors"
                >
                  <Handshake className="w-4 h-4" strokeWidth={2} />
                  Recruitment Partner Login
                </Link>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6">
                <h4 className="font-heading font-bold text-slate-900 text-sm">Quick share</h4>
                <p className="text-xs text-slate-500 mt-1">Refer this opening to a friend.</p>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.share) navigator.share({ title: job.title, url: window.location.href }).catch(() => {});
                    else { navigator.clipboard?.writeText(window.location.href); }
                  }}
                  data-testid="job-share"
                  className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-primary hover:text-primary-hover"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share this job
                </button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply-form" className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-primary">Apply Now</p>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight">
              Submit your application
            </h2>
            <p className="mt-3 text-slate-600">It takes less than 2 minutes.</p>
          </div>

          <div className="mt-10 bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-sm">
            <ApplyForm job={job} />
          </div>
        </div>
      </section>
    </>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="mt-1 flex items-center gap-2 text-slate-900 font-medium">
        <Icon className="w-4 h-4 text-primary shrink-0" /> <span className="truncate">{value}</span>
      </div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 tracking-tight">{title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-slate-700 leading-relaxed">
          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 shrink-0" /> <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
